import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, Mountain, Check } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import InquiryForm from "@/components/InquiryForm";
import useReveal from "@/hooks/useReveal";
import { fetchPackage } from "@/lib/api";
import { buildWaLink } from "@/lib/constants";

export default function PackageDetail() {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [error, setError] = useState(null);
  useReveal([pkg?.slug]);

  useEffect(() => {
    setPkg(null);
    setError(null);
    fetchPackage(slug)
      .then(setPkg)
      .catch((e) => setError(e?.response?.status === 404 ? "not-found" : "error"));
  }, [slug]);

  if (error === "not-found") {
    return (
      <div className="pt-40 min-h-[70vh] max-w-3xl mx-auto px-6 text-center">
        <p className="eyebrow mb-4">404 · Package Not Found</p>
        <h1 className="font-serif text-4xl text-[#1F3A5F]">
          We couldn't find that journey.
        </h1>
        <Link
          to="/packages"
          className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#1F3A5F] border-b border-[#1F3A5F] pb-1"
        >
          <ArrowLeft size={14} /> Back to all packages
        </Link>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="pt-32 min-h-[70vh] max-w-7xl mx-auto px-6">
        <div className="aspect-[16/9] bg-[#1F3A5F]/5 animate-pulse" />
      </div>
    );
  }

  const waMsg = `Hi iExplore, I'd like to enquire about the "${pkg.title}" (${pkg.duration}) package.`;
  const themeChips = pkg.theme.split("|").map((t) => t.trim()).filter(Boolean);

  return (
    <div data-testid={`package-detail-${pkg.slug}`}>
      {/* HERO BANNER */}
      <section className="relative h-[80vh] min-h-[560px] overflow-hidden grain">
        <img
          src={pkg.image}
          alt={pkg.title}
          className="absolute inset-0 w-full h-full object-cover kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#11233B]/95 via-[#1F3A5F]/55 to-[#1F3A5F]/30" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 h-full flex flex-col justify-end pb-16 md:pb-24 text-[#FAF4ED]">
          <Link
            to="/packages"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#FAF4ED]/80 hover:text-[#FAF4ED] mb-8"
          >
            <ArrowLeft size={14} /> All Packages
          </Link>
          <p className="eyebrow text-[#E8B89B] mb-5">{pkg.duration}</p>
          <h1
            data-testid="package-title"
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl"
          >
            {pkg.title}
          </h1>
          <p className="mt-4 text-base md:text-lg text-[#FAF4ED]/85 max-w-2xl">
            {pkg.subtitle}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            {themeChips.map((c) => (
              <span
                key={c}
                className="text-[10px] uppercase tracking-[0.22em] border border-[#FAF4ED]/30 px-3 py-1.5"
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-8">
            {/* OVERVIEW */}
            <div className="reveal">
              <p className="eyebrow mb-3">Overview</p>
              <h2 className="font-serif font-light text-3xl md:text-4xl text-[#1F3A5F] leading-tight">
                {pkg.subtitle}
              </h2>
              <p className="mt-6 text-[#5A6B82] leading-relaxed text-base md:text-lg">
                {pkg.overview}
              </p>

              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px bg-[#1F3A5F]/10">
                <div className="bg-[#FAF4ED] p-5">
                  <Clock size={16} className="text-[#E8B89B]" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A6B82] mt-3">
                    Duration
                  </p>
                  <p className="font-serif text-2xl text-[#1F3A5F] mt-1">
                    {pkg.nights}N · {pkg.days}D
                  </p>
                </div>
                <div className="bg-[#FAF4ED] p-5">
                  <Mountain size={16} className="text-[#E8B89B]" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A6B82] mt-3">
                    Region
                  </p>
                  <p className="font-serif text-2xl text-[#1F3A5F] mt-1">Ladakh</p>
                </div>
                <div className="bg-[#FAF4ED] p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A6B82]">
                    Days
                  </p>
                  <p className="font-serif text-2xl text-[#1F3A5F] mt-1">
                    {pkg.itinerary.length}
                  </p>
                </div>
                <div className="bg-[#FAF4ED] p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#5A6B82]">
                    From
                  </p>
                  <p className="font-serif text-2xl text-[#1F3A5F] mt-1">
                    ₹{pkg.price_from.toLocaleString("en-IN")}
                  </p>
                </div>
              </div>
            </div>

            {/* HIGHLIGHTS */}
            <div className="mt-20 reveal">
              <p className="eyebrow mb-3">Trip Highlights</p>
              <h3 className="font-serif font-light text-3xl md:text-4xl text-[#1F3A5F]">
                What you'll experience
              </h3>
              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {pkg.highlights.map((h) => (
                  <li
                    key={h}
                    className="flex gap-3 text-[#11233B]/90 border-t border-[#1F3A5F]/10 pt-4"
                  >
                    <Check size={18} strokeWidth={1.6} className="text-[#E8B89B] mt-1 flex-shrink-0" />
                    <span className="text-base">{h}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* DAY-WISE ITINERARY */}
            <div className="mt-20 reveal" data-testid="itinerary-section">
              <p className="eyebrow mb-3">Day-by-Day</p>
              <h3 className="font-serif font-light text-3xl md:text-4xl text-[#1F3A5F]">
                Detailed itinerary
              </h3>

              <Accordion
                type="multiple"
                defaultValue={["day-1"]}
                className="mt-10"
              >
                {pkg.itinerary.map((day) => (
                  <AccordionItem
                    key={day.day}
                    value={`day-${day.day}`}
                    data-testid={`day-${day.day}`}
                    className="border-b border-[#1F3A5F]/15"
                  >
                    <AccordionTrigger className="hover:no-underline group py-6 text-left">
                      <div className="flex items-baseline gap-6">
                        <span className="font-serif text-3xl md:text-4xl text-[#E8B89B] group-hover:text-[#1F3A5F] transition-colors w-16 flex-shrink-0">
                          {String(day.day).padStart(2, "0")}
                        </span>
                        <div>
                          <span className="block text-[10px] uppercase tracking-[0.22em] text-[#5A6B82]">
                            Day {day.day}
                          </span>
                          <span className="block font-serif text-xl md:text-2xl text-[#1F3A5F] mt-1.5 leading-snug pr-4">
                            {day.title}
                          </span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-8">
                      <p className="text-[#5A6B82] leading-relaxed text-base md:pl-[5.5rem] pr-2">
                        {day.content}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>

          {/* STICKY SIDEBAR */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="bg-white border border-[#1F3A5F]/10 p-8">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#5A6B82]">
                  Starting From
                </p>
                <p className="font-serif text-5xl text-[#1F3A5F] mt-1">
                  ₹{pkg.price_from.toLocaleString("en-IN")}
                </p>
                <p className="text-xs text-[#5A6B82] mt-2">
                  per person · customisable for group size, season & inclusions
                </p>

                <div className="mt-8 space-y-3">
                  <a
                    href={buildWaLink(waMsg)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="detail-whatsapp"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bf5a] text-white px-6 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
                  >
                    WhatsApp Inquiry
                  </a>
                  <a
                    href="#enquire"
                    data-testid="detail-book"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#1F3A5F] hover:bg-[#11233B] text-[#FAF4ED] px-6 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
                  >
                    Book / Customise
                  </a>
                </div>

                <ul className="mt-8 space-y-3 text-xs text-[#5A6B82] border-t border-[#1F3A5F]/10 pt-6">
                  <li className="flex gap-2">
                    <Check size={14} className="text-[#E8B89B] mt-0.5" /> Local
                    Ladakhi expertise
                  </li>
                  <li className="flex gap-2">
                    <Check size={14} className="text-[#E8B89B] mt-0.5" />
                    Acclimatisation-paced itinerary
                  </li>
                  <li className="flex gap-2">
                    <Check size={14} className="text-[#E8B89B] mt-0.5" /> 24/7
                    on-trip support
                  </li>
                  <li className="flex gap-2">
                    <Check size={14} className="text-[#E8B89B] mt-0.5" /> Honest,
                    transparent pricing
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section
        id="enquire"
        className="bg-white border-y border-[#1F3A5F]/10 scroll-mt-28"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-28 reveal">
          <p className="eyebrow mb-3">Plan This Journey</p>
          <h2 className="font-serif font-light text-4xl md:text-5xl text-[#1F3A5F] leading-[1.1]">
            Let's get you to {pkg.title.split("—")[0].trim()}.
          </h2>
          <p className="mt-5 text-[#5A6B82] max-w-xl">
            Share a few details and we'll build a custom plan within hours —
            with real prices, real availability, and no pressure.
          </p>

          <div className="mt-12">
            <InquiryForm
              packageSlug={pkg.slug}
              packageTitle={`${pkg.title} (${pkg.duration})`}
              source={`detail:${pkg.slug}`}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
